var __defProp = Object.defineProperty;
var __export = (target, all) => {
  for (var name in all)
    __defProp(target, name, { get: all[name], enumerable: true });
};

// src/sdk/sdk.ts
import {
  createCloakClient,
  Configuration
} from "@axis-finance/cloak";
import { createTRPCProxyClient, httpBatchLink } from "@trpc/client";
import { deployments as deployments3 } from "@axis-finance/deployments";
import {
  Environment,
  getCloakServer,
  getCuratorServer
} from "@axis-finance/env";

// src/core/index.ts
var core_exports = {};
__export(core_exports, {
  abort: () => abort_exports,
  bid: () => bid_exports,
  cancel: () => cancel_exports,
  claimBids: () => claim_bids_exports,
  create: () => create_exports,
  createClient: () => createClient,
  refundBid: () => refund_bid_exports,
  settle: () => settle_exports,
  tokens: () => tokens_exports,
  utils: () => utils_exports
});

// src/core/public-client.ts
import { createPublicClient, http } from "viem";
import * as chains from "viem/chains";

// src/types.ts
import * as v5 from "valibot";

// ../../node_modules/@trpc/server/dist/getCauseFromUnknown-2d66414a.mjs
function isObject(value) {
  return !!value && !Array.isArray(value) && typeof value === "object";
}
var UnknownCauseError = class extends Error {
};
function getCauseFromUnknown(cause) {
  if (cause instanceof Error) {
    return cause;
  }
  const type = typeof cause;
  if (type === "undefined" || type === "function" || cause === null) {
    return void 0;
  }
  if (type !== "object") {
    return new Error(String(cause));
  }
  if (isObject(cause)) {
    const err = new UnknownCauseError();
    for (const key in cause) {
      err[key] = cause[key];
    }
    return err;
  }
  return void 0;
}

// ../../node_modules/@trpc/server/dist/TRPCError-98d44758.mjs
function getTRPCErrorFromUnknown(cause) {
  if (cause instanceof TRPCError) {
    return cause;
  }
  if (cause instanceof Error && cause.name === "TRPCError") {
    return cause;
  }
  const trpcError = new TRPCError({
    code: "INTERNAL_SERVER_ERROR",
    cause
  });
  if (cause instanceof Error && cause.stack) {
    trpcError.stack = cause.stack;
  }
  return trpcError;
}
var TRPCError = class extends Error {
  constructor(opts) {
    const cause = getCauseFromUnknown(opts.cause);
    const message = opts.message ?? cause?.message ?? opts.code;
    super(message, {
      cause
    });
    this.code = opts.code;
    this.name = "TRPCError";
    if (!this.cause) {
      this.cause = cause;
    }
  }
};

// ../../node_modules/@trpc/server/dist/codes-c924c3db.mjs
function invert(obj) {
  const newObj = /* @__PURE__ */ Object.create(null);
  for (const key in obj) {
    const v26 = obj[key];
    newObj[v26] = key;
  }
  return newObj;
}
var TRPC_ERROR_CODES_BY_KEY = {
  /**
  * Invalid JSON was received by the server.
  * An error occurred on the server while parsing the JSON text.
  */
  PARSE_ERROR: -32700,
  /**
  * The JSON sent is not a valid Request object.
  */
  BAD_REQUEST: -32600,
  // Internal JSON-RPC error
  INTERNAL_SERVER_ERROR: -32603,
  NOT_IMPLEMENTED: -32603,
  // Implementation specific errors
  UNAUTHORIZED: -32001,
  FORBIDDEN: -32003,
  NOT_FOUND: -32004,
  METHOD_NOT_SUPPORTED: -32005,
  TIMEOUT: -32008,
  CONFLICT: -32009,
  PRECONDITION_FAILED: -32012,
  PAYLOAD_TOO_LARGE: -32013,
  UNPROCESSABLE_CONTENT: -32022,
  TOO_MANY_REQUESTS: -32029,
  CLIENT_CLOSED_REQUEST: -32099
};
var TRPC_ERROR_CODES_BY_NUMBER = invert(TRPC_ERROR_CODES_BY_KEY);

// ../../node_modules/@trpc/server/dist/index-f91d720c.mjs
var TRPC_ERROR_CODES_BY_NUMBER2 = invert(TRPC_ERROR_CODES_BY_KEY);
var JSONRPC2_TO_HTTP_CODE = {
  PARSE_ERROR: 400,
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  NOT_FOUND: 404,
  FORBIDDEN: 403,
  METHOD_NOT_SUPPORTED: 405,
  TIMEOUT: 408,
  CONFLICT: 409,
  PRECONDITION_FAILED: 412,
  PAYLOAD_TOO_LARGE: 413,
  UNPROCESSABLE_CONTENT: 422,
  TOO_MANY_REQUESTS: 429,
  CLIENT_CLOSED_REQUEST: 499,
  INTERNAL_SERVER_ERROR: 500,
  NOT_IMPLEMENTED: 501
};
function getStatusCodeFromKey(code) {
  return JSONRPC2_TO_HTTP_CODE[code] ?? 500;
}
function getHTTPStatusCodeFromError(error) {
  return getStatusCodeFromKey(error.code);
}
var noop = () => {
};
function createInnerProxy(callback, path) {
  const proxy = new Proxy(noop, {
    get(_obj, key) {
      if (typeof key !== "string" || key === "then") {
        return void 0;
      }
      return createInnerProxy(callback, [
        ...path,
        key
      ]);
    },
    apply(_1, _2, args) {
      const isApply = path[path.length - 1] === "apply";
      return callback({
        args: isApply ? args.length >= 2 ? args[1] : [] : args,
        path: isApply ? path.slice(0, -1) : path
      });
    }
  });
  return proxy;
}
var createRecursiveProxy = (callback) => createInnerProxy(callback, []);
var createFlatProxy = (callback) => {
  return new Proxy(noop, {
    get(_obj, name) {
      if (typeof name !== "string" || name === "then") {
        return void 0;
      }
      return callback(name);
    }
  });
};

// ../../node_modules/@trpc/server/dist/config-d5fdbd39.mjs
function getDataTransformer(transformer) {
  if ("input" in transformer) {
    return transformer;
  }
  return {
    input: transformer,
    output: transformer
  };
}
var defaultTransformer = {
  _default: true,
  input: {
    serialize: (obj) => obj,
    deserialize: (obj) => obj
  },
  output: {
    serialize: (obj) => obj,
    deserialize: (obj) => obj
  }
};
var defaultFormatter = ({ shape }) => {
  return shape;
};
function omitPrototype(obj) {
  return Object.assign(/* @__PURE__ */ Object.create(null), obj);
}
var procedureTypes = [
  "query",
  "mutation",
  "subscription"
];
function isRouter(procedureOrRouter) {
  return "router" in procedureOrRouter._def;
}
var emptyRouter = {
  _ctx: null,
  _errorShape: null,
  _meta: null,
  queries: {},
  mutations: {},
  subscriptions: {},
  errorFormatter: defaultFormatter,
  transformer: defaultTransformer
};
var reservedWords = [
  /**
  * Then is a reserved word because otherwise we can't return a promise that returns a Proxy
  * since JS will think that `.then` is something that exists
  */
  "then"
];
function createRouterFactory(config) {
  return function createRouterInner(procedures) {
    const reservedWordsUsed = new Set(Object.keys(procedures).filter((v26) => reservedWords.includes(v26)));
    if (reservedWordsUsed.size > 0) {
      throw new Error("Reserved words used in `router({})` call: " + Array.from(reservedWordsUsed).join(", "));
    }
    const routerProcedures = omitPrototype({});
    function recursiveGetPaths(procedures2, path = "") {
      for (const [key, procedureOrRouter] of Object.entries(procedures2 ?? {})) {
        const newPath = `${path}${key}`;
        if (isRouter(procedureOrRouter)) {
          recursiveGetPaths(procedureOrRouter._def.procedures, `${newPath}.`);
          continue;
        }
        if (routerProcedures[newPath]) {
          throw new Error(`Duplicate key: ${newPath}`);
        }
        routerProcedures[newPath] = procedureOrRouter;
      }
    }
    recursiveGetPaths(procedures);
    const _def = {
      _config: config,
      router: true,
      procedures: routerProcedures,
      ...emptyRouter,
      record: procedures,
      queries: Object.entries(routerProcedures).filter((pair) => pair[1]._def.query).reduce((acc, [key, val]) => ({
        ...acc,
        [key]: val
      }), {}),
      mutations: Object.entries(routerProcedures).filter((pair) => pair[1]._def.mutation).reduce((acc, [key, val]) => ({
        ...acc,
        [key]: val
      }), {}),
      subscriptions: Object.entries(routerProcedures).filter((pair) => pair[1]._def.subscription).reduce((acc, [key, val]) => ({
        ...acc,
        [key]: val
      }), {})
    };
    const router2 = {
      ...procedures,
      _def,
      createCaller(ctx) {
        return createCallerFactory()(router2)(ctx);
      },
      getErrorShape(opts) {
        const { path, error } = opts;
        const { code } = opts.error;
        const shape = {
          message: error.message,
          code: TRPC_ERROR_CODES_BY_KEY[code],
          data: {
            code,
            httpStatus: getHTTPStatusCodeFromError(error)
          }
        };
        if (config.isDev && typeof opts.error.stack === "string") {
          shape.data.stack = opts.error.stack;
        }
        if (typeof path === "string") {
          shape.data.path = path;
        }
        return this._def._config.errorFormatter({
          ...opts,
          shape
        });
      }
    };
    return router2;
  };
}
function callProcedure(opts) {
  const { type, path } = opts;
  if (!(path in opts.procedures) || !opts.procedures[path]?._def[type]) {
    throw new TRPCError({
      code: "NOT_FOUND",
      message: `No "${type}"-procedure on path "${path}"`
    });
  }
  const procedure = opts.procedures[path];
  return procedure(opts);
}
function createCallerFactory() {
  return function createCallerInner(router2) {
    const def = router2._def;
    return function createCaller(ctx) {
      const proxy = createRecursiveProxy(({ path, args }) => {
        if (path.length === 1 && procedureTypes.includes(path[0])) {
          return callProcedure({
            procedures: def.procedures,
            path: args[0],
            rawInput: args[1],
            ctx,
            type: path[0]
          });
        }
        const fullPath = path.join(".");
        const procedure = def.procedures[fullPath];
        let type = "query";
        if (procedure._def.mutation) {
          type = "mutation";
        } else if (procedure._def.subscription) {
          type = "subscription";
        }
        return procedure({
          path: fullPath,
          rawInput: args[0],
          ctx,
          type
        });
      });
      return proxy;
    };
  };
}
var isServerDefault = typeof window === "undefined" || "Deno" in window || globalThis.process?.env?.NODE_ENV === "test" || !!globalThis.process?.env?.JEST_WORKER_ID || !!globalThis.process?.env?.VITEST_WORKER_ID;

// ../../node_modules/@trpc/server/dist/index.mjs
function getParseFn(procedureParser) {
  const parser2 = procedureParser;
  if (typeof parser2 === "function") {
    return parser2;
  }
  if (typeof parser2.parseAsync === "function") {
    return parser2.parseAsync.bind(parser2);
  }
  if (typeof parser2.parse === "function") {
    return parser2.parse.bind(parser2);
  }
  if (typeof parser2.validateSync === "function") {
    return parser2.validateSync.bind(parser2);
  }
  if (typeof parser2.create === "function") {
    return parser2.create.bind(parser2);
  }
  if (typeof parser2.assert === "function") {
    return (value) => {
      parser2.assert(value);
      return value;
    };
  }
  throw new Error("Could not find a validator fn");
}
function mergeWithoutOverrides(obj1, ...objs) {
  const newObj = Object.assign(/* @__PURE__ */ Object.create(null), obj1);
  for (const overrides of objs) {
    for (const key in overrides) {
      if (key in newObj && newObj[key] !== overrides[key]) {
        throw new Error(`Duplicate key ${key}`);
      }
      newObj[key] = overrides[key];
    }
  }
  return newObj;
}
function createMiddlewareFactory() {
  function createMiddlewareInner(middlewares) {
    return {
      _middlewares: middlewares,
      unstable_pipe(middlewareBuilderOrFn) {
        const pipedMiddleware = "_middlewares" in middlewareBuilderOrFn ? middlewareBuilderOrFn._middlewares : [
          middlewareBuilderOrFn
        ];
        return createMiddlewareInner([
          ...middlewares,
          ...pipedMiddleware
        ]);
      }
    };
  }
  function createMiddleware(fn) {
    return createMiddlewareInner([
      fn
    ]);
  }
  return createMiddleware;
}
function isPlainObject(obj) {
  return obj && typeof obj === "object" && !Array.isArray(obj);
}
function createInputMiddleware(parse2) {
  const inputMiddleware = async ({ next, rawInput, input }) => {
    let parsedInput;
    try {
      parsedInput = await parse2(rawInput);
    } catch (cause) {
      throw new TRPCError({
        code: "BAD_REQUEST",
        cause
      });
    }
    const combinedInput = isPlainObject(input) && isPlainObject(parsedInput) ? {
      ...input,
      ...parsedInput
    } : parsedInput;
    return next({
      input: combinedInput
    });
  };
  inputMiddleware._type = "input";
  return inputMiddleware;
}
function createOutputMiddleware(parse2) {
  const outputMiddleware = async ({ next }) => {
    const result = await next();
    if (!result.ok) {
      return result;
    }
    try {
      const data = await parse2(result.data);
      return {
        ...result,
        data
      };
    } catch (cause) {
      throw new TRPCError({
        message: "Output validation failed",
        code: "INTERNAL_SERVER_ERROR",
        cause
      });
    }
  };
  outputMiddleware._type = "output";
  return outputMiddleware;
}
var middlewareMarker = "middlewareMarker";
function createNewBuilder(def1, def2) {
  const { middlewares = [], inputs, meta, ...rest } = def2;
  return createBuilder({
    ...mergeWithoutOverrides(def1, rest),
    inputs: [
      ...def1.inputs,
      ...inputs ?? []
    ],
    middlewares: [
      ...def1.middlewares,
      ...middlewares
    ],
    meta: def1.meta && meta ? {
      ...def1.meta,
      ...meta
    } : meta ?? def1.meta
  });
}
function createBuilder(initDef = {}) {
  const _def = {
    inputs: [],
    middlewares: [],
    ...initDef
  };
  return {
    _def,
    input(input) {
      const parser2 = getParseFn(input);
      return createNewBuilder(_def, {
        inputs: [
          input
        ],
        middlewares: [
          createInputMiddleware(parser2)
        ]
      });
    },
    output(output) {
      const parseOutput = getParseFn(output);
      return createNewBuilder(_def, {
        output,
        middlewares: [
          createOutputMiddleware(parseOutput)
        ]
      });
    },
    meta(meta) {
      return createNewBuilder(_def, {
        meta
      });
    },
    /**
    * @deprecated
    * This functionality is deprecated and will be removed in the next major version.
    */
    unstable_concat(builder) {
      return createNewBuilder(_def, builder._def);
    },
    use(middlewareBuilderOrFn) {
      const middlewares = "_middlewares" in middlewareBuilderOrFn ? middlewareBuilderOrFn._middlewares : [
        middlewareBuilderOrFn
      ];
      return createNewBuilder(_def, {
        middlewares
      });
    },
    query(resolver) {
      return createResolver({
        ..._def,
        query: true
      }, resolver);
    },
    mutation(resolver) {
      return createResolver({
        ..._def,
        mutation: true
      }, resolver);
    },
    subscription(resolver) {
      return createResolver({
        ..._def,
        subscription: true
      }, resolver);
    }
  };
}
function createResolver(_def, resolver) {
  const finalBuilder = createNewBuilder(_def, {
    resolver,
    middlewares: [
      async function resolveMiddleware(opts) {
        const data = await resolver(opts);
        return {
          marker: middlewareMarker,
          ok: true,
          data,
          ctx: opts.ctx
        };
      }
    ]
  });
  return createProcedureCaller(finalBuilder._def);
}
var codeblock = `
This is a client-only function.
If you want to call this function on the server, see https://trpc.io/docs/server/server-side-calls
`.trim();
function createProcedureCaller(_def) {
  const procedure = async function resolve(opts) {
    if (!opts || !("rawInput" in opts)) {
      throw new Error(codeblock);
    }
    const callRecursive = async (callOpts = {
      index: 0,
      ctx: opts.ctx
    }) => {
      try {
        const middleware = _def.middlewares[callOpts.index];
        const result2 = await middleware({
          ctx: callOpts.ctx,
          type: opts.type,
          path: opts.path,
          rawInput: callOpts.rawInput ?? opts.rawInput,
          meta: _def.meta,
          input: callOpts.input,
          next(_nextOpts) {
            const nextOpts = _nextOpts;
            return callRecursive({
              index: callOpts.index + 1,
              ctx: nextOpts && "ctx" in nextOpts ? {
                ...callOpts.ctx,
                ...nextOpts.ctx
              } : callOpts.ctx,
              input: nextOpts && "input" in nextOpts ? nextOpts.input : callOpts.input,
              rawInput: nextOpts && "rawInput" in nextOpts ? nextOpts.rawInput : callOpts.rawInput
            });
          }
        });
        return result2;
      } catch (cause) {
        return {
          ok: false,
          error: getTRPCErrorFromUnknown(cause),
          marker: middlewareMarker
        };
      }
    };
    const result = await callRecursive();
    if (!result) {
      throw new TRPCError({
        code: "INTERNAL_SERVER_ERROR",
        message: "No result from middlewares - did you forget to `return next()`?"
      });
    }
    if (!result.ok) {
      throw result.error;
    }
    return result.data;
  };
  procedure._def = _def;
  procedure.meta = _def.meta;
  return procedure;
}
function mergeRouters(...routerList) {
  const record = mergeWithoutOverrides({}, ...routerList.map((r) => r._def.record));
  const errorFormatter = routerList.reduce((currentErrorFormatter, nextRouter) => {
    if (nextRouter._def._config.errorFormatter && nextRouter._def._config.errorFormatter !== defaultFormatter) {
      if (currentErrorFormatter !== defaultFormatter && currentErrorFormatter !== nextRouter._def._config.errorFormatter) {
        throw new Error("You seem to have several error formatters");
      }
      return nextRouter._def._config.errorFormatter;
    }
    return currentErrorFormatter;
  }, defaultFormatter);
  const transformer = routerList.reduce((prev, current) => {
    if (current._def._config.transformer && current._def._config.transformer !== defaultTransformer) {
      if (prev !== defaultTransformer && prev !== current._def._config.transformer) {
        throw new Error("You seem to have several transformers");
      }
      return current._def._config.transformer;
    }
    return prev;
  }, defaultTransformer);
  const router2 = createRouterFactory({
    errorFormatter,
    transformer,
    isDev: routerList.some((r) => r._def._config.isDev),
    allowOutsideOfServer: routerList.some((r) => r._def._config.allowOutsideOfServer),
    isServer: routerList.some((r) => r._def._config.isServer),
    $types: routerList[0]?._def._config.$types
  })(record);
  return router2;
}
var TRPCBuilder = class _TRPCBuilder {
  context() {
    return new _TRPCBuilder();
  }
  meta() {
    return new _TRPCBuilder();
  }
  create(options) {
    return createTRPCInner()(options);
  }
};
var initTRPC = new TRPCBuilder();
function createTRPCInner() {
  return function initTRPCInner(runtime) {
    const errorFormatter = runtime?.errorFormatter ?? defaultFormatter;
    const transformer = getDataTransformer(runtime?.transformer ?? defaultTransformer);
    const config = {
      transformer,
      isDev: runtime?.isDev ?? globalThis.process?.env?.NODE_ENV !== "production",
      allowOutsideOfServer: runtime?.allowOutsideOfServer ?? false,
      errorFormatter,
      isServer: runtime?.isServer ?? isServerDefault,
      /**
      * @internal
      */
      $types: createFlatProxy((key) => {
        throw new Error(`Tried to access "$types.${key}" which is not available at runtime`);
      })
    };
    {
      const isServer = runtime?.isServer ?? isServerDefault;
      if (!isServer && runtime?.allowOutsideOfServer !== true) {
        throw new Error(`You're trying to use @trpc/server in a non-server environment. This is not supported by default.`);
      }
    }
    return {
      /**
      * These are just types, they can't be used
      * @internal
      */
      _config: config,
      /**
      * Builder object for creating procedures
      * @see https://trpc.io/docs/server/procedures
      */
      procedure: createBuilder({
        meta: runtime?.defaultMeta
      }),
      /**
      * Create reusable middlewares
      * @see https://trpc.io/docs/server/middlewares
      */
      middleware: createMiddlewareFactory(),
      /**
      * Create a router
      * @see https://trpc.io/docs/server/routers
      */
      router: createRouterFactory(config),
      /**
      * Merge Routers
      * @see https://trpc.io/docs/server/merging-routers
      */
      mergeRouters,
      /**
      * Create a server-side caller for a router
      * @see https://trpc.io/docs/server/server-side-calls
      */
      createCallerFactory: createCallerFactory()
    };
  };
}

// src/core/create/schema.ts
import * as v4 from "valibot";

// src/core/schema.ts
import * as v from "valibot";
var BytesSchema = v.custom(
  (input) => typeof input === "string" ? /^0x[a-fA-F0-9]*$/.test(input) : false
);
var AddressSchema = v.custom(
  (input) => typeof input === "string" ? /^0x[a-fA-F0-9]{40}$/.test(input) : false
);
var IPFSCIDV1Schema = v.custom(
  (input) => typeof input === "string" ? /^b[a-z2-7]{58}$/i.test(input) : false
);

// src/modules/auctions/fixed-price/schema.ts
import * as v2 from "valibot";
import { AuctionType } from "@axis-finance/types";
var schema = v2.object({
  type: v2.literal(AuctionType.FIXED_PRICE_BATCH),
  price: v2.bigint(),
  minFillPercent: v2.number()
});

// src/modules/auctions/sealed-bid/schema.ts
import * as v3 from "valibot";
import { AuctionType as AuctionType2 } from "@axis-finance/types";
var schema2 = v3.object({
  type: v3.literal(AuctionType2.SEALED_BID),
  minFillPercent: v3.number(),
  minBidSize: v3.bigint(),
  minPrice: v3.bigint(),
  publicKey: v3.object({
    x: v3.bigint(),
    y: v3.bigint()
  })
});

// src/core/create/schema.ts
var Bytes5Schema = v4.custom(
  (input) => typeof input === "string" ? /^0x[a-fA-F0-9]*$/.test(input) : false
);
var AuctionMetadataSchema = v4.object({
  id: v4.optional(v4.string()),
  name: v4.string(),
  description: v4.string(),
  tagline: v4.string(),
  allowlist: v4.optional(v4.array(v4.array(v4.string()))),
  links: v4.object({
    projectLogo: v4.pipe(v4.string(), v4.url()),
    payoutTokenLogo: v4.pipe(v4.string(), v4.url()),
    projectBanner: v4.pipe(v4.string(), v4.url()),
    website: v4.pipe(v4.string(), v4.url()),
    twitter: v4.optional(v4.pipe(v4.string(), v4.url())),
    discord: v4.optional(v4.pipe(v4.string(), v4.url())),
    farcaster: v4.optional(v4.pipe(v4.string(), v4.url()))
  })
});
var CommonAuctionSchema = v4.object({
  curator: AddressSchema,
  referrerFee: v4.number(),
  startDate: v4.date(),
  endDate: v4.date(),
  isCapacityDenominatedInQuoteToken: v4.optional(v4.boolean()),
  capacity: v4.string(),
  metadata: AuctionMetadataSchema
});
var schema3 = v4.object({
  chainId: v4.number(),
  auction: v4.variant("type", [
    v4.object({
      ...CommonAuctionSchema.entries,
      ...schema2.entries
    }),
    v4.object({
      ...CommonAuctionSchema.entries,
      ...schema.entries
    })
  ]),
  tokens: v4.object({
    base: v4.object({
      address: AddressSchema,
      decimals: v4.number()
    }),
    quote: v4.object({
      address: AddressSchema,
      decimals: v4.number()
    })
  }),
  // TODO: handle specific callback modules like we did auction modules
  callback: v4.object({
    address: AddressSchema,
    data: BytesSchema
  }),
  // TODO: handle specific derivative modules like we did auction modules
  derivative: v4.object({
    type: Bytes5Schema,
    params: BytesSchema,
    wrap: v4.boolean()
  })
});

// src/utils/metadata-client.ts
import {
  FleekSdk,
  ApplicationAccessTokenService
} from "@fleek-platform/sdk/browser";
var MetadataClient = class {
  sdk;
  constructor({ fleekApplicationClientId }) {
    if (!fleekApplicationClientId) {
      throw new Error("Fleek application client ID is required");
    }
    const applicationService = new ApplicationAccessTokenService({
      clientId: fleekApplicationClientId
    });
    this.sdk = new FleekSdk({
      accessTokenService: applicationService
    });
  }
  async save({ metadata, id }) {
    try {
      JSON.parse(metadata);
    } catch (e) {
      throw new Error("Invalid JSON metadata");
    }
    try {
      const blob = new Blob([metadata], { type: "application/json" });
      const file = new File([blob], id ?? `unknown-${Date.now()}`, {
        type: "application/json"
      });
      const result = await this.sdk.storage().uploadFile({ file });
      return result.pin.cid;
    } catch (error) {
      throw new Error(`Failed to store metadata: ${error}`);
    }
  }
};

// src/types.ts
var SdkError = class extends Error {
  issues;
  constructor(message, issues = void 0) {
    super(message);
    this.name = "OriginSdkError";
    this.issues = issues;
  }
};
var t = initTRPC.create({
  isServer: false,
  allowOutsideOfServer: true
});
var CuratorProfileSchema = v5.object({
  curatorProfilePayload: v5.string(),
  ipfsCid: v5.string()
});
var router = t.router({
  storeAuctionInfo: t.procedure.input(v5.parser(AuctionMetadataSchema)).output(v5.parser(v5.string())).mutation(() => ""),
  bustAxisFollowingCache: t.procedure.input(v5.parser(v5.object({ pwd: v5.string() }))).query(() => true),
  axisFollowing: t.procedure.input(v5.parser(v5.void())).query(() => true),
  getSigningSignatureForCurator: t.procedure.input(v5.parser(CuratorProfileSchema)).output(
    v5.parser(
      v5.object({
        signature: v5.string()
      })
    )
  ).mutation(() => ({
    signature: ""
  }))
});

// src/core/utils.ts
var utils_exports = {};
__export(utils_exports, {
  getChainById: () => getChainById,
  getContractAddresses: () => getContractAddresses,
  getDeploymentByChainId: () => getDeploymentByChainId,
  getLaunchId: () => getLaunchId
});
import { deployments } from "@axis-finance/deployments";
var getContractAddresses = (chainId, deployments4) => {
  return deployments4[chainId]?.addresses;
};
var getChainById = (chains3, chainId) => {
  return chains3 ? Object.values(chains3).find((chain) => chain.id === chainId) : void 0;
};
var getDeploymentByChainId = (chainId) => {
  const deployment = deployments[chainId];
  if (!deployment)
    throw new Error(`Unable to find deployment for chainId ${chainId}`);
  return deployment;
};
var formatChainName = (str) => str.toLowerCase().replace(/ /g, "-");
var formatAuctionId = (chainName, auctionHouseAddress, lotId) => {
  const formattedChainName = formatChainName(chainName);
  return `${formattedChainName}-${auctionHouseAddress.toLowerCase()}-${lotId}`;
};
var getLaunchId = (chainId, lotId) => {
  const deployment = getDeploymentByChainId(chainId);
  return formatAuctionId(
    deployment.name,
    deployment.addresses.batchAuctionHouse,
    lotId
  );
};

// src/core/public-client.ts
var createClient = (chainId, config) => {
  const chain = getChainById(chains, chainId);
  if (chain === void 0) {
    throw new SdkError(`Chain with id ${chainId} not found`);
  }
  return createPublicClient({
    chain,
    transport: http(),
    ...config
  });
};

// src/core/bid/index.ts
var bid_exports = {};
__export(bid_exports, {
  abi: () => abi_exports,
  getConfig: () => getConfig,
  schema: () => schema4,
  utils: () => utils_exports2
});

// src/core/bid/schema.ts
import * as v6 from "valibot";
import { AuctionType as AuctionType3 } from "@axis-finance/types";
var schema4 = v6.object({
  lotId: v6.number(),
  amountIn: v6.pipe(v6.bigint(), v6.minValue(BigInt(1))),
  amountOut: v6.bigint(),
  chainId: v6.number(),
  auctionType: v6.optional(v6.enum_(AuctionType3)),
  bidderAddress: AddressSchema,
  referrerAddress: AddressSchema,
  signedPermit2Approval: v6.optional(v6.string()),
  callbackData: BytesSchema
});

// src/core/bid/abi.ts
var abi_exports = {};
__export(abi_exports, {
  ENCRYPTED_BID_TYPE_ABI: () => ENCRYPTED_BID_TYPE_ABI
});
var ENCRYPTED_BID_TYPE_ABI = [
  { name: "encryptedBid", type: "uint256" },
  {
    name: "bidPublicKey",
    type: "tuple",
    internalType: "struct Point",
    components: [
      {
        name: "x",
        type: "uint256"
      },
      {
        name: "y",
        type: "uint256"
      }
    ]
  }
];

// src/core/bid/utils.ts
var utils_exports2 = {};
__export(utils_exports2, {
  encodeEncryptedBid: () => encodeEncryptedBid,
  getEncryptedBid: () => getEncryptedBid
});
import { fromHex, toHex, encodeAbiParameters } from "viem";
import { getAuctionHouse } from "@axis-finance/deployments";
var getEncryptedBid = async (params, cloakClient) => {
  let result;
  try {
    const { lotId, amountIn, amountOut, chainId, bidderAddress, auctionType } = params;
    const auctionHouseAddress = getAuctionHouse({
      chainId,
      auctionType
    })?.address;
    result = await cloakClient.keysApi.encryptLotIdPost({
      xChainId: chainId,
      xAuctionHouse: auctionHouseAddress,
      lotId,
      encryptRequest: {
        amount: toHex(amountIn),
        amountOut: toHex(amountOut),
        bidder: bidderAddress
      }
    });
  } catch (error) {
    console.error(error);
    throw new SdkError("Failed to encrypt bid via cloak service");
  }
  if (!result || !result.ciphertext || !result.x || !result.y) {
    throw new SdkError("Failed to encrypt bid via cloak service");
  }
  return result;
};
var encodeEncryptedBid = (encryptedBid) => {
  return encodeAbiParameters(abi_exports.ENCRYPTED_BID_TYPE_ABI, [
    fromHex(encryptedBid.ciphertext, "bigint"),
    {
      x: fromHex(encryptedBid.x, "bigint"),
      y: fromHex(encryptedBid.y, "bigint")
    }
  ]);
};

// src/core/bid/get-config.ts
import * as v7 from "valibot";
import { AuctionType as AuctionType4 } from "@axis-finance/types";
import { abis } from "@axis-finance/abis";
import { getAuctionHouse as getAuctionHouse2 } from "@axis-finance/deployments";
import { toHex as toHex2 } from "viem";
var getConfig = async (params, cloak) => {
  const parsedParams = v7.safeParse(schema4, params);
  if (!parsedParams.success) {
    throw new SdkError(
      "Invalid parameters supplied to getConfig()",
      parsedParams.issues
    );
  }
  const {
    lotId,
    amountIn,
    amountOut,
    bidderAddress,
    referrerAddress,
    chainId,
    auctionType,
    callbackData
  } = params;
  const auctionHouseAddress = getAuctionHouse2({
    chainId,
    auctionType
  })?.address;
  if (auctionHouseAddress === void 0 || auctionHouseAddress === "0x") {
    throw new SdkError(
      `Auction house contract address not found for chainId ${chainId} and auctionType ${auctionType}`
    );
  }
  const shouldEncryptBid = auctionType === AuctionType4.SEALED_BID;
  const paramsToEncrypt = {
    lotId,
    amountIn,
    amountOut,
    chainId,
    bidderAddress,
    auctionType
  };
  const encryptedBid = shouldEncryptBid ? encodeEncryptedBid(await getEncryptedBid(paramsToEncrypt, cloak)) : void 0;
  return {
    abi: abis.batchAuctionHouse,
    address: auctionHouseAddress,
    functionName: "bid",
    args: [
      {
        lotId: BigInt(lotId),
        bidder: bidderAddress,
        referrer: referrerAddress,
        amount: amountIn,
        auctionData: encryptedBid || toHex2(""),
        permit2Data: toHex2("")
        // TODO: handle permit2Data
      },
      callbackData
    ]
  };
};

// src/core/claim-bids/index.ts
var claim_bids_exports = {};
__export(claim_bids_exports, {
  getConfig: () => getConfig2,
  schema: () => schema5
});

// src/core/claim-bids/schema.ts
import * as v8 from "valibot";
import { AuctionType as AuctionType5 } from "@axis-finance/types";
var schema5 = v8.object({
  lotId: v8.number(),
  bids: v8.array(v8.number()),
  chainId: v8.number(),
  auctionType: v8.optional(v8.enum_(AuctionType5))
});

// src/core/claim-bids/get-config.ts
import * as v9 from "valibot";
import { abis as abis2 } from "@axis-finance/abis";
import { getAuctionHouse as getAuctionHouse3 } from "@axis-finance/deployments";
var getConfig2 = (params) => {
  const parsedParams = v9.safeParse(schema5, params);
  if (!parsedParams.success) {
    throw new SdkError(
      "Invalid parameters supplied to getConfig()",
      parsedParams.issues
    );
  }
  const { lotId, bids, chainId, auctionType } = params;
  const auctionHouseAddress = getAuctionHouse3({
    chainId,
    auctionType
  })?.address;
  return {
    abi: abis2.batchAuctionHouse,
    address: auctionHouseAddress,
    functionName: "claimBids",
    args: [BigInt(lotId), bids.map(BigInt)]
  };
};

// src/core/settle/index.ts
var settle_exports = {};
__export(settle_exports, {
  getConfig: () => getConfig3,
  schema: () => schema6
});

// src/core/settle/schema.ts
import { AuctionType as AuctionType6 } from "@axis-finance/types";
import * as v10 from "valibot";
var schema6 = v10.object({
  lotId: v10.number(),
  numBids: v10.number(),
  chainId: v10.number(),
  callbackData: AddressSchema,
  auctionType: v10.optional(v10.enum_(AuctionType6))
});

// src/core/settle/get-config.ts
import * as v11 from "valibot";
import { abis as abis3 } from "@axis-finance/abis";
import { getAuctionHouse as getAuctionHouse4 } from "@axis-finance/deployments";
var getConfig3 = (params) => {
  const parsedParams = v11.safeParse(schema6, params);
  if (!parsedParams.success) {
    throw new SdkError(
      "Invalid parameters supplied to getConfig()",
      parsedParams.issues
    );
  }
  const { lotId, numBids, chainId, callbackData, auctionType } = params;
  const auctionHouseAddress = getAuctionHouse4({
    chainId,
    auctionType
  })?.address;
  return {
    abi: abis3.batchAuctionHouse,
    address: auctionHouseAddress,
    functionName: "settle",
    args: [BigInt(lotId), BigInt(numBids), callbackData]
  };
};

// src/core/refund-bid/index.ts
var refund_bid_exports = {};
__export(refund_bid_exports, {
  getConfig: () => getConfig4,
  schema: () => schema7
});

// src/core/refund-bid/schema.ts
import * as v12 from "valibot";
import { AuctionType as AuctionType7 } from "@axis-finance/types";
var schema7 = v12.object({
  lotId: v12.number(),
  bidId: v12.number(),
  bidIndex: v12.number(),
  chainId: v12.number(),
  auctionType: v12.optional(v12.enum_(AuctionType7))
});

// src/core/refund-bid/get-config.ts
import * as v13 from "valibot";
import { abis as abis4 } from "@axis-finance/abis";
import { getAuctionHouse as getAuctionHouse5 } from "@axis-finance/deployments";
var getConfig4 = (params) => {
  const parsedParams = v13.safeParse(schema7, params);
  if (!parsedParams.success) {
    throw new SdkError(
      "Invalid parameters supplied to getConfig()",
      parsedParams.issues
    );
  }
  const { lotId, bidId, bidIndex, chainId, auctionType } = params;
  const auctionHouseAddress = getAuctionHouse5({
    chainId,
    auctionType
  })?.address;
  return {
    abi: abis4.batchAuctionHouse,
    address: auctionHouseAddress,
    functionName: "refundBid",
    args: [BigInt(lotId), BigInt(bidId), BigInt(bidIndex)]
  };
};

// src/core/abort/index.ts
var abort_exports = {};
__export(abort_exports, {
  getConfig: () => getConfig5,
  schema: () => schema8
});

// src/core/abort/schema.ts
import { AuctionType as AuctionType8 } from "@axis-finance/types";
import * as v14 from "valibot";
var schema8 = v14.object({
  lotId: v14.number(),
  chainId: v14.number(),
  auctionType: v14.enum_(AuctionType8)
});

// src/core/abort/get-config.ts
import * as v15 from "valibot";
import { abis as abis5 } from "@axis-finance/abis";
import { getAuctionHouse as getAuctionHouse6 } from "@axis-finance/deployments";
var getConfig5 = (params) => {
  const parsedParams = v15.safeParse(schema8, params);
  if (!parsedParams.success) {
    throw new SdkError(
      "Invalid parameters supplied to getConfig()",
      parsedParams.issues
    );
  }
  const { lotId, chainId, auctionType } = params;
  const auctionHouseAddress = getAuctionHouse6({
    chainId,
    auctionType
  })?.address;
  return {
    abi: abis5.batchAuctionHouse,
    address: auctionHouseAddress,
    functionName: "abort",
    args: [BigInt(lotId)]
  };
};

// src/core/cancel/index.ts
var cancel_exports = {};
__export(cancel_exports, {
  getConfig: () => getConfig6,
  schema: () => schema9
});

// src/core/cancel/schema.ts
import * as v16 from "valibot";
import { AuctionType as AuctionType9 } from "@axis-finance/types";
var schema9 = v16.object({
  chainId: v16.number(),
  lotId: v16.number(),
  auctionType: v16.optional(v16.enum_(AuctionType9)),
  callback: v16.optional(BytesSchema)
});

// src/core/cancel/get-config.ts
import * as v17 from "valibot";
import { getAuctionHouse as getAuctionHouse7 } from "@axis-finance/deployments";
import { abis as abis6 } from "@axis-finance/abis";
var getConfig6 = (params) => {
  const parsedParams = v17.safeParse(schema9, params);
  if (!parsedParams.success) {
    throw new SdkError(
      "Invalid parameters supplied to getConfig()",
      parsedParams.issues
    );
  }
  const { chainId, lotId, callback, auctionType } = params;
  const auctionHouse = getAuctionHouse7({ chainId, auctionType });
  return {
    abi: abis6.batchAuctionHouse,
    address: auctionHouse.address,
    functionName: "cancel",
    args: [BigInt(lotId), callback ?? "0x"]
  };
};

// src/core/create/index.ts
var create_exports = {};
__export(create_exports, {
  getConfig: () => getConfig7,
  schema: () => schema3
});

// src/core/create/get-config.ts
import { parseUnits as parseUnits2 } from "viem";
import * as v18 from "valibot";
import { getAuctionHouse as getAuctionHouse8 } from "@axis-finance/deployments";
import { abis as abis7 } from "@axis-finance/abis";

// src/core/create/utils.ts
import { toHex as toHex3 } from "viem";
var getTimestamp = (date2) => Math.floor(date2.getTime() / 1e3);
var toKeycode = (keycode) => {
  return toHex3(keycode, { size: 5 });
};

// src/modules/auctions/utils.ts
import { AuctionType as AuctionType12 } from "@axis-finance/types";

// src/modules/auctions/fixed-price/module.ts
import { encodeAbiParameters as encodeAbiParameters2 } from "viem";
import { AuctionType as AuctionType10 } from "@axis-finance/types";

// src/modules/auctions/fixed-price/abi.ts
var abi = [
  {
    name: "AuctionDataParams",
    internalType: "struct AuctionDataParams",
    type: "tuple",
    components: [
      { name: "price", type: "uint256" },
      { name: "minFillPercent", type: "uint24" }
    ]
  }
];

// src/modules/auctions/fixed-price/module.ts
var getAuctionModuleParams = ({ auction }) => {
  if (auction.type !== AuctionType10.FIXED_PRICE_BATCH) {
    throw new SdkError(
      "fixed-price getAuctionModuleParams() should only be called on fixed-price auctions. Received type: " + auction.type
    );
  }
  if (auction.price == null || auction.minFillPercent) {
    throw new SdkError(
      "fixed-price getAuctionModuleParams() requires a price and minFillPercent"
    );
  }
  const params = [
    {
      price: auction.price,
      minFillPercent: toBasisPoints(auction.minFillPercent)
    }
  ];
  return encodeAbiParameters2(abi, params);
};

// src/modules/auctions/sealed-bid/module.ts
import { encodeAbiParameters as encodeAbiParameters3, parseUnits } from "viem";
import { AuctionType as AuctionType11 } from "@axis-finance/types";

// src/modules/auctions/sealed-bid/abi.ts
var abi2 = [
  {
    name: "AuctionDataParams",
    internalType: "struct AuctionDataParams",
    type: "tuple",
    components: [
      { name: "minPrice", type: "uint256" },
      { name: "minFillPercent", type: "uint24" },
      { name: "minBidSize", type: "uint256" },
      {
        name: "publicKey",
        internalType: "struct Point",
        type: "tuple",
        components: [
          { name: "x", internalType: "uint256", type: "uint256" },
          { name: "y", internalType: "uint256", type: "uint256" }
        ]
      }
    ]
  }
];

// src/modules/auctions/sealed-bid/module.ts
var getAuctionModuleParams2 = ({ auction, tokens }) => {
  if (auction.type !== AuctionType11.SEALED_BID) {
    throw new SdkError(
      "sealed-bid getAuctionModuleParams() should only be called on sealed-bid auctions. Received type: " + auction.type
    );
  }
  if (auction.minBidSize !== null || auction.minFillPercent !== null || auction.minPrice !== null) {
    throw new SdkError(
      "sealed-bid getAuctionModuleParams() requires a price and minFillPercent"
    );
  }
  const params = [
    {
      minFillPercent: toBasisPoints(auction.minFillPercent),
      minBidSize: parseUnits(auction.minBidSize, tokens.quote.decimals),
      minPrice: parseUnits(auction.minPrice, tokens.quote.decimals),
      publicKey: auction.publicKey
    }
  ];
  return encodeAbiParameters3(abi2, params);
};

// src/modules/auctions/utils.ts
function toBasisPoints(percentage) {
  const _percentage = typeof percentage === "string" ? parseFloat(percentage) : percentage;
  return _percentage * 100;
}
var getAuctionModuleParams3 = (params) => {
  const { auction } = params;
  if (auction.type === AuctionType12.SEALED_BID) {
    return getAuctionModuleParams2(params);
  }
  if (auction.type === AuctionType12.FIXED_PRICE_BATCH) {
    return getAuctionModuleParams(params);
  }
  return "0x";
};

// src/core/create/get-config.ts
var getConfig7 = async (params, metadataClient) => {
  const parsedParams = v18.safeParse(schema3, params);
  if (!parsedParams.success) {
    throw new SdkError(
      "Invalid parameters supplied to getConfig()",
      parsedParams.issues
    );
  }
  const { chainId, auction, tokens, callback, derivative } = params;
  const auctionType = auction.type;
  const auctionKeyCode = toKeycode(auctionType);
  const auctionHouse = getAuctionHouse8({ chainId, auctionType });
  const auctionModuleParams = getAuctionModuleParams3(params);
  const metadataIpfsHash = metadataClient != null ? await metadataClient.save({
    metadata: params.auction.metadata.toString(),
    id: params.auction.metadata.id
  }) : "";
  return {
    abi: abis7.batchAuctionHouse,
    address: auctionHouse.address,
    functionName: "auction",
    args: [
      {
        auctionType: auctionKeyCode,
        baseToken: tokens.base.address,
        quoteToken: tokens.quote.address,
        curator: auction.curator,
        referrerFee: auction.referrerFee,
        callbacks: callback.address,
        callbackData: callback.data,
        derivativeType: derivative.type,
        derivativeParams: derivative.params,
        wrapDerivative: derivative.wrap
      },
      {
        start: getTimestamp(auction.startDate),
        duration: getTimestamp(auction.endDate) - getTimestamp(auction.startDate),
        capacityInQuote: false,
        // Batch auctions do not allow capacity in quote
        capacity: parseUnits2(auction.capacity, tokens.base.decimals),
        implParams: auctionModuleParams
      },
      metadataIpfsHash
    ]
  };
};

// src/core/tokens/index.ts
var tokens_exports = {};
__export(tokens_exports, {
  functions: () => functions_exports,
  schema: () => schema_exports,
  utils: () => utils_exports3
});

// src/core/tokens/schema.ts
var schema_exports = {};
__export(schema_exports, {
  GetTokenPriceSchema: () => GetTokenPriceSchema,
  GetTokenPricesSchema: () => GetTokenPricesSchema
});
import * as v19 from "valibot";
var GetTokenPriceSchema = v19.object({
  token: v19.object({
    chainId: v19.number(),
    address: AddressSchema,
    symbol: v19.string(),
    decimals: v19.number(),
    name: v19.string(),
    totalSupply: v19.optional(v19.string()),
    logoURI: v19.optional(v19.string()),
    mintable: v19.optional(v19.boolean())
  }),
  timestamp: v19.optional(v19.number())
});
var GetTokenPricesSchema = v19.object({
  tokens: v19.array(GetTokenPriceSchema)
});

// src/core/tokens/functions/index.ts
var functions_exports = {};
__export(functions_exports, {
  getTokenPrice: () => getTokenPrice,
  getTokenPrices: () => getTokenPrices,
  isUsdToken: () => isUsdToken
});

// src/core/tokens/functions/get-token-price.ts
import {
  isTestnet,
  getMainnetTokenFromSymbol
} from "@axis-finance/deployments";

// src/core/tokens/utils.ts
var utils_exports3 = {};
__export(utils_exports3, {
  fetchTokenPrices: () => fetchTokenPrices
});
import * as chains2 from "viem/chains";
var getMainnetName = (chainId) => {
  return Object.values(chains2).find((chain) => chain.id === chainId)?.name;
};
var unixTimestampNow = Math.floor(Date.now() / 1e3);
var fetchTokenPrices = async (tokens, timestamp = unixTimestampNow) => {
  if (!tokens || tokens?.length === 0) {
    throw new Error("tokens is required");
  }
  const preparedTokens = tokens.map(
    (token) => `${getMainnetName(token.chainId)}:${token.address}`
  );
  const tokenUrlQuery = preparedTokens.join(",");
  const response = await fetch(
    `https://coins.llama.fi/prices/historical/${timestamp}/${tokenUrlQuery}?searchWidth=1h`
    // TODO: search width
  );
  const json = await response.json();
  return preparedTokens.map((token) => json?.coins[token]?.price);
};

// src/core/tokens/functions/get-token-price.ts
var usdTokens = [
  "USDC",
  "USDT",
  "USDD",
  "TUSD",
  "PYUSD",
  "USDe",
  "USDP",
  "CRVUSD",
  "DAI",
  "USDB",
  "LUSD",
  "FRAX",
  "GUSD",
  "BUSD"
];
var isUsdToken = (symbol) => usdTokens.includes(symbol.toUpperCase());
var MAINNET_USDC = {
  chainId: 1,
  decimals: 6,
  symbol: "USDC",
  name: "USD Coin",
  address: "0xa0b86991c6218b36c1d19d4a2e9eb0ce3606eb48"
};
var MAINNET_WETH = {
  chainId: 1,
  decimals: 18,
  symbol: "WETH",
  name: "Wrapped Ether",
  address: "0xc02aaa39b223fe8d0a0e5c4f27ead9083c756cc2"
};
var MAINNET_MANTLE = {
  chainId: 1,
  decimals: 18,
  symbol: "MNT",
  name: "Mantle",
  address: "0x3c3a81e81dc49a522a592e7622a7e711c06bf354"
};
var adjustUnsupportedTokens = (tokens) => {
  return tokens.map((token) => {
    if (isUsdToken(token.symbol)) {
      return MAINNET_USDC;
    }
    if (token.symbol === "WETH") {
      return MAINNET_WETH;
    }
    if (token.symbol === "WMNT") {
      return MAINNET_MANTLE;
    }
    if (isTestnet(token.chainId)) {
      return getMainnetTokenFromSymbol(token.symbol) || token;
    }
    return token;
  });
};
var getTokenPrices = async (tokens, timestamp) => {
  const adjustedTokens = adjustUnsupportedTokens(tokens);
  return fetchTokenPrices(adjustedTokens, timestamp);
};
var getTokenPrice = async (token, timestamp) => {
  const [price] = await getTokenPrices([token], timestamp);
  return price;
};

// src/periphery/index.ts
var periphery_exports = {};
__export(periphery_exports, {
  callbacks: () => callbacks_exports
});

// src/periphery/callbacks/index.ts
var callbacks_exports = {};
__export(callbacks_exports, {
  setMerkleRoot: () => set_merkle_root_exports
});

// src/periphery/callbacks/set-merkle-root/index.ts
var set_merkle_root_exports = {};
__export(set_merkle_root_exports, {
  getConfig: () => getConfig8,
  schema: () => schema11
});

// src/periphery/callbacks/set-merkle-root/schema.ts
import * as v21 from "valibot";

// src/utils/merkle-tree/schema.ts
import * as v20 from "valibot";
var typeSchema = v20.tupleWithRest(
  [v20.literal("address")],
  v20.literal("uint256")
);
var schema10 = v20.object({
  types: typeSchema,
  values: v20.array(v20.array(v20.string()))
});

// src/periphery/callbacks/set-merkle-root/schema.ts
var schema11 = v21.object({
  lotId: v21.number(),
  callback: AddressSchema,
  allowlist: schema10
});

// src/periphery/callbacks/set-merkle-root/get-config.ts
import * as v23 from "valibot";

// src/utils/merkle-tree/generate-merkle-root.ts
import * as v22 from "valibot";
import { StandardMerkleTree } from "@openzeppelin/merkle-tree";
import { isAddress } from "viem";
var generateMerkleRoot = (args) => {
  const { values, types } = v22.parse(schema10, args);
  validateAllowlistEntries(args);
  const { root } = StandardMerkleTree.of(values, types);
  return root;
};
function validateAllowlistEntries({ values, types }) {
  const errors = {
    invalidEntry: [],
    invalidAddress: [],
    invalidAmount: []
  };
  values.forEach((value, i) => {
    const lineNumber = i + 1;
    if (types.length !== value.length) errors.invalidEntry.push(lineNumber);
    if (!isAddress(value[0])) errors.invalidAddress.push(lineNumber);
    if (value.length === 2 && !isValidBigInt(value[1]))
      errors.invalidAmount.push(lineNumber);
  });
  const errorMessage = joinErrors(errors);
  if (errorMessage) {
    throw new Error(
      `Failed to generate Merkle Tree Root due to:
${errorMessage}`
    );
  }
}
function isValidBigInt(value) {
  try {
    BigInt(value);
    return true;
  } catch {
    return false;
  }
}
function joinErrors(errors) {
  return Object.entries(errors).filter(([, lines]) => lines.length > 0).map(([key, lines]) => {
    const messages = {
      invalidEntry: "Missing address or amount",
      invalidAddress: "Invalid address",
      invalidAmount: "Invalid amount"
    };
    return `${messages[key]} on line(s) ${lines.join(", ")}
`;
  }).join("");
}

// src/periphery/callbacks/set-merkle-root/get-config.ts
import { abis as abis8 } from "@axis-finance/abis";
var getConfig8 = (params) => {
  const parsedParams = v23.safeParse(schema11, params);
  if (!parsedParams.success) {
    throw new SdkError(
      "Invalid parameters supplied to getConfig()",
      parsedParams.issues
    );
  }
  const { lotId, callback, allowlist } = params;
  const merkleRoot = generateMerkleRoot(allowlist);
  return {
    abi: abis8.merkleAllowlist,
    address: callback,
    functionName: "setMerkleRoot",
    args: [BigInt(lotId), merkleRoot]
  };
};

// src/registry/index.ts
var registry_exports = {};
__export(registry_exports, {
  registerAuction: () => register_auction_exports
});

// src/registry/register-auction/index.ts
var register_auction_exports = {};
__export(register_auction_exports, {
  getConfig: () => getConfig9,
  schema: () => schema12
});

// src/registry/register-auction/schema.ts
import * as v24 from "valibot";
var schema12 = v24.object({
  auctionHouse: AddressSchema,
  lotId: v24.number(),
  ipfsCID: IPFSCIDV1Schema,
  isTestnet: v24.boolean()
});

// src/registry/register-auction/get-config.ts
import * as v25 from "valibot";
import { abis as abis9 } from "@axis-finance/abis";
import { deployments as deployments2 } from "@axis-finance/deployments";
import { base, baseSepolia } from "viem/chains";
var getConfig9 = (params) => {
  const parsedParams = v25.safeParse(schema12, params);
  if (!parsedParams.success) {
    throw new SdkError(
      "Invalid parameters supplied to getConfig()",
      parsedParams.issues
    );
  }
  const chain = params.isTestnet ? baseSepolia : base;
  const deployment = deployments2[chain.id];
  const { lotId, auctionHouse, ipfsCID } = params;
  return {
    chainId: chain.id,
    address: deployment.registry,
    abi: abis9.axisMetadataRegistry,
    functionName: "registerAuction",
    args: [auctionHouse, BigInt(lotId), ipfsCID]
  };
};

// src/sdk/sdk.ts
var defaultConfig = {
  environment: Environment.PRODUCTION,
  cloak: {
    url: getCloakServer(Environment.PRODUCTION).url
  },
  curator: {
    url: getCuratorServer(Environment.PRODUCTION).url
  }
};
var OriginSdk = class {
  config;
  core;
  periphery;
  registry;
  deployments;
  cloakClient;
  metadataClient;
  curatorClient;
  constructor(_config = defaultConfig, _core = core_exports, _periphery = periphery_exports, _registry = registry_exports, _deployments = deployments3) {
    this.config = _config;
    this.core = _core;
    this.deployments = _deployments;
    this.periphery = _periphery;
    this.registry = _registry;
    this.cloakClient = createCloakClient(
      new Configuration({ basePath: _config.cloak.url })
    );
    this.curatorClient = createTRPCProxyClient({
      links: [
        httpBatchLink({
          url: _config.curator.url
        })
      ]
    });
    this.metadataClient = _config.metadata?.fleekApplicationClientId ? new MetadataClient({
      fleekApplicationClientId: _config.metadata?.fleekApplicationClientId
    }) : void 0;
  }
  async getTokenPrice(params) {
    return this.core.tokens.functions.getTokenPrice(
      params.token,
      params.timestamp
    );
  }
  isUsdToken(symbol) {
    return this.core.tokens.functions.isUsdToken(symbol);
  }
  /**
   * Gets the contract config required to execute a bid transaction on the auction house smart contract from unprimed parameters.
   *
   * @side_effects
   * - Encrypts the bid via the cloak service
   *
   * @todo
   * - Add cloak service repo link in side effect text above
   *
   * @param params - Unprimed bid parameters
   * @returns Contract config for the bid transaction
   *
   * @example
   * import { sdk } from "./sdk"
   *
   * try {
   *   const config = await sdk.bid({
   *     lotId: 1,
   *     chainId: 1,
   *     amountIn: 100,
   *     amountOut: 100,
   *     auctionType: AuctionType.SEALED_BID,
   *     bidderAddress: "0x123...",
   *     referrerAddress: "0x456...",
   *     signedPermit2Approval: "0x789...",
   *   })
   * } catch (error: SdkError) {
   *   console.log(error.message, error.issues)
   * }
   */
  async bid(params) {
    return this.core.bid.getConfig(params, this.cloakClient);
  }
  /**
   * Gets the contract config required to execute a claimBids transaction on the auction house smart contract.
   *
   * @param params claimBids parameters
   * @returns Contract config for the claimBids transaction
   *
   * @example
   * import { sdk } from "./sdk"
   *
   * try {
   *   const config = sdk.claimBids({
   *     lotId: 1,
   *     bids: [1, 2, 3],
   *     chainId: 1,
   *   })
   * } catch (error: SdkError) {
   *   console.log(error.message, error.issues)
   * }
   */
  claimBids(params) {
    return this.core.claimBids.getConfig(params);
  }
  /**
   * Gets the contract config required to execute a refundBid transaction on the auction house smart contract.
   *
   * @param params refundBid parameters
   * @returns Contract config for the refundBid transaction
   *
   * @example
   * import { sdk } from "./sdk"
   *
   * try {
   *   const config = sdk.refundBid({
   *     lotId: 1,
   *     bidId: 10,
   *     bidIndex: 1,
   *     chainId: 1,
   *   })
   * } catch (error: SdkError) {
   *   console.log(error.message, error.issues)
   * }
   */
  refundBid(params) {
    return this.core.refundBid.getConfig(params);
  }
  /**
   * Gets the contract config required to execute a settle transaction on the auction house smart contract.
   *
   * @param params settle parameters
   * @returns Contract config for the settle transaction
   *
   * @example
   * import { sdk } from "./sdk"
   *
   * try {
   *   const config = sdk.settle({
   *     lotId: 1,
   *     chainId: 1,
   *     numBids: 10,
   *     callbackData: "0x...",
   *   })
   * } catch (error: SdkError) {
   *   console.log(error.message, error.issues)
   * }
   */
  settle(params) {
    return this.core.settle.getConfig(params);
  }
  /**
   * Gets the contract config required to execute an abort transaction on the auction house smart contract.
   * Aborting an auction ends it and enables bidders to reclaim their bids.
   *
   * @param params abort parameters
   * @returns Contract config for the abort transaction
   *
   * @example
   * import { sdk } from "./sdk"
   *
   * try {
   *   const config = sdk.abort({
   *     lotId: 1,
   *     chainId: 1,
   *   })
   * } catch (error: SdkError) {
   *   console.log(error.message, error.issues)
   * }
   */
  abort(params) {
    return this.core.abort.getConfig(params);
  }
  /**
   * Gets the contract config required to execute a cancel transaction on the auction house smart contract.
   * Cancelling an auction ends the auction, and is only possible before a bidder has bid on it.
   *
   * @param params cancel parameters
   * @returns Contract config for the cancel transaction
   *
   * @example
   * import { sdk } from "./sdk"
   *
   * try {
   *   const config = sdk.cancel({
   *     lotId: 1,
   *     chainId: 1,
   *   })
   * } catch (error: SdkError) {
   *   console.log(error.message, error.issues)
   * }
   */
  cancel(params) {
    return this.core.cancel.getConfig(params);
  }
  /**
   * // TODO
   * todo
   *
   * @param params create parameters
   * @returns Contract config for the create transaction
   *
   * @example
   * import { sdk } from "./sdk"
   *
   * try {
   *   const config = await sdk.create({
   *     //TODO: 1,
   *   })
   * } catch (error: SdkError) {
   *   console.log(error.message, error.issues)
   * }
   */
  async create(params) {
    return this.core.create.getConfig(params, this.metadataClient);
  }
  setMerkleRoot(params) {
    return this.periphery.callbacks.setMerkleRoot.getConfig(params);
  }
  async saveMetadata(params) {
    return this.metadataClient?.save(params);
  }
  registerAuction(params) {
    return this.registry.registerAuction.getConfig(params);
  }
};
var createSdk = (config) => new OriginSdk(config);
export {
  OriginSdk,
  createSdk
};
//# sourceMappingURL=index.js.map